import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import { resolveImageUrl } from "../../utils/driveImage";
import { toAnchorId } from "../../utils/markdown";
import styles from "./MarkdownContent.module.css";

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Extrai o texto plano de filhos React (para gerar o id de headings). */
function childrenToText(children) {
    return React.Children.toArray(children)
        .map((child) => {
            if (typeof child === "string" || typeof child === "number") {
                return String(child);
            }
            if (child && child.props && child.props.children) {
                return childrenToText(child.props.children);
            }
            return "";
        })
        .join("");
}

/** Reconhece o ID de um vídeo do YouTube a partir de várias formas de URL. */
function parseYoutubeId(rawUrl) {
    if (!rawUrl || typeof rawUrl !== "string") return null;
    const url = rawUrl.trim();

    const patterns = [
        /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
        /(?:youtu\.be\/)([\w-]{11})/,
        /(?:youtube\.com\/embed\/)([\w-]{11})/,
        /(?:youtube\.com\/shorts\/)([\w-]{11})/,
    ];

    for (const re of patterns) {
        const m = re.exec(url);
        if (m) return m[1];
    }
    return null;
}

// ── Plugin remark: directives (cor / destaque) + embed de YouTube ───────────────

/**
 * Transforma:
 *  - `:color[texto]{value=#hex}`  → <span style="color:…">
 *  - `:mark[texto]`               → <mark>
 *  - parágrafo contendo só uma URL do YouTube → componente <youtube>
 */
function remarkBlogDirectives() {
    return (tree) => {
        walk(tree);
    };

    function walk(node) {
        if (!node || typeof node !== "object") return;

        handleDirective(node);
        handleYoutube(node);

        if (Array.isArray(node.children)) {
            for (const child of node.children) walk(child);
        }
    }

    function handleDirective(node) {
        if (
            node.type !== "textDirective" &&
            node.type !== "leafDirective" &&
            node.type !== "containerDirective"
        ) {
            return;
        }

        const attrs = node.attributes || {};
        const data = node.data || (node.data = {});

        if (node.name === "color") {
            const value = attrs.value || attrs.name || "inherit";
            data.hName = "span";
            data.hProperties = { style: `color:${value}` };
        } else if (node.name === "mark") {
            data.hName = "mark";
            data.hProperties = {};
        } else {
            // Diretiva desconhecida: renderiza só o conteúdo, sem quebrar o texto.
            data.hName = "span";
            data.hProperties = {};
        }
    }

    function handleYoutube(node) {
        if (node.type !== "paragraph" || !Array.isArray(node.children)) return;
        if (node.children.length !== 1) return;

        const child = node.children[0];
        let url = null;
        if (child.type === "link") url = child.url;
        else if (child.type === "text") url = child.value;

        const id = parseYoutubeId(url);
        if (!id) return;

        node.data = {
            hName: "youtube",
            hProperties: { videoid: id },
        };
        node.children = [];
    }
}

// ── Componentes customizados ────────────────────────────────────────────────────

function Heading({ level, children, ...props }) {
    const id = toAnchorId(childrenToText(children));
    const Tag = `h${level}`;
    const className = level === 2 ? styles.contentH2 : styles.contentH3;
    return (
        <Tag id={id} className={className} {...props}>
            {children}
        </Tag>
    );
}

function MarkdownImage({ src, alt, title }) {
    const resolved = resolveImageUrl(src);
    if (!resolved) return null;
    return (
        <figure className={styles.figure}>
            <img src={resolved} alt={alt || ""} loading="lazy" />
            {title && <figcaption>{title}</figcaption>}
        </figure>
    );
}

function YoutubeEmbed({ videoid }) {
    if (!videoid) return null;
    return (
        <div className={styles.youtubeEmbed}>
            <iframe
                src={`https://www.youtube.com/embed/${videoid}`}
                title="Vídeo do YouTube"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
            />
        </div>
    );
}

const COMPONENTS = {
    h2: (props) => <Heading level={2} {...props} />,
    h3: (props) => <Heading level={3} {...props} />,
    img: MarkdownImage,
    youtube: YoutubeEmbed,
    mark: (props) => <mark className={styles.mark} {...props} />,
    a: ({ node, ...props }) => (
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        <a target="_blank" rel="noopener noreferrer" {...props} />
    ),
    table: (props) => (
        <div className={styles.tableWrap}>
            <table {...props} />
        </div>
    ),
};

const REMARK_PLUGINS = [remarkGfm, remarkDirective, remarkBlogDirectives];

// ── Componente principal ────────────────────────────────────────────────────────

export default function MarkdownContent({ content, className }) {
    return (
        <div className={`${styles.content} ${className || ""}`}>
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
                {content || ""}
            </ReactMarkdown>
        </div>
    );
}
